import Signal from 'signal-js';
import { mix, clamp } from '@immutabl3/utils';
import EndInertiaUtil from './EndInertiaUtil.js';
import MouseWheelInertiaUtil from './MouseWheelInertiaUtil.js';
import ScrollEvent from './ScrollEvent.js';
import { options as defaultOptions, } from './options.js';
export default function ScrollSensor(defaults = {}) {
    const signal = Signal();
    const options = { ...defaultOptions, ...defaults };
    const { element = globalThis?.document?.body } = options;
    const eventMouseWheel = 'onmousewheel' in globalThis?.document ? 'mousewheel' : 'wheel';
    let paused = false;
    let scrollLeft = 0;
    let scrollTop = 0;
    let mouseIsScrolling = false;
    let mouseXLastPosition = -1;
    let mouseYLastPosition = -1;
    const mouseScrollEndInertiaUtil = EndInertiaUtil();
    let mouseWheelXLastPosition = -1;
    let mouseWheelYLastPosition = -1;
    const mouseWheelEndInertiaUtil = MouseWheelInertiaUtil(options);
    let touchXLastPosition = -1;
    let touchYLastPosition = -1;
    const touchEndInertiaUtil = EndInertiaUtil();
    const move = (xDistance, yDistance) => {
        if (paused)
            return;
        scrollTop = clamp(scrollTop + yDistance, options.minScrollTop, options.maxScrollTop);
        scrollLeft = clamp(scrollLeft + xDistance, options.minScrollLeft, options.maxScrollLeft);
        signal.emit('scroll', ScrollEvent(scrollTop, scrollLeft, yDistance, xDistance));
    };
    const handleMousewheel = (event) => {
        if (!options.mouseWheelIsEnable)
            return;
        mouseWheelXLastPosition = event.deltaX * options.mouseWheelXSpeed;
        mouseWheelYLastPosition = event.deltaY * options.mouseWheelYSpeed;
        move(mouseWheelXLastPosition, mouseWheelYLastPosition);
        if (options.inertiaScrollEnabled) {
            mouseWheelEndInertiaUtil.stop();
            mouseWheelEndInertiaUtil.push(mouseWheelXLastPosition, mouseWheelYLastPosition);
        }
    };
    const handleMouseDown = (event) => {
        if (!options.mouseMoveIsEnable)
            return;
        mouseXLastPosition = event.clientX;
        mouseYLastPosition = event.clientY;
        mouseIsScrolling = true;
        mouseScrollEndInertiaUtil.stop();
    };
    const handleMouseMove = (event) => {
        if (!mouseIsScrolling)
            return;
        const xDistance = mouseXLastPosition - event.clientX;
        const yDistance = mouseYLastPosition - event.clientY;
        move(xDistance * options.mouseMoveXSpeed, yDistance * options.mouseMoveYSpeed);
        mouseXLastPosition = event.clientX;
        mouseYLastPosition = event.clientY;
        mouseScrollEndInertiaUtil.push(scrollLeft, scrollTop);
    };
    const handleMouseUp = () => {
        mouseIsScrolling = false;
        mouseScrollEndInertiaUtil.end();
    };
    const handleTouchStart = (event) => {
        if (!options.touchIsEnable)
            return;
        touchXLastPosition = Math.round(event.touches[0].clientX);
        touchYLastPosition = Math.round(event.touches[0].clientY);
        touchEndInertiaUtil.stop();
    };
    const handleTouchMove = (event) => {
        if (!options.touchIsEnable)
            return;
        const clientX = Math.round(event.touches[0].clientX);
        const clientY = Math.round(event.touches[0].clientY);
        const xDistance = touchXLastPosition - clientX;
        const yDistance = touchYLastPosition - clientY;
        move(xDistance * options.touchXSpeed, yDistance * options.touchYSpeed);
        touchXLastPosition = clientX;
        touchYLastPosition = clientY;
        touchEndInertiaUtil.push(scrollLeft, scrollTop);
    };
    const handleTouchEnd = () => {
        touchEndInertiaUtil.end();
    };
    const handleInertiaMove = (event) => {
        move(event.xDistance, event.yDistance);
    };
    const handleKeydown = (event) => {
        if (globalThis.document.activeElement !== element)
            return;
        if (event.shiftKey || event.ctrlKey || event.altKey || event.isComposing)
            return;
        if (event.key === ' ' && !event.metaKey)
            return move(0, globalThis.innerHeight);
        if (event.key === 'ArrowDown' && event.metaKey)
            return move(0, options.maxScrollTop);
        if (event.key === 'ArrowUp' && event.metaKey)
            return move(0, -options.maxScrollTop);
        if (event.key === 'ArrowRight' && event.metaKey)
            return move(0, options.maxScrollLeft);
        if (event.key === 'ArrowLeft' && event.metaKey)
            return move(0, -options.maxScrollLeft);
        if (event.key === 'ArrowDown' && !event.metaKey)
            return move(0, options.arrowScrollY);
        if (event.key === 'ArrowUp' && !event.metaKey)
            return move(0, -options.arrowScrollY);
        if (event.key === 'ArrowRight' && !event.metaKey)
            return move(0, options.arrowScrollX);
        if (event.key === 'ArrowLeft' && !event.metaKey)
            return move(0, -options.arrowScrollX);
    };
    const bind = () => {
        element.addEventListener('keydown', handleKeydown);
        element.addEventListener(eventMouseWheel, handleMousewheel);
        element.addEventListener('mousedown', handleMouseDown);
        element.addEventListener('mousemove', handleMouseMove);
        element.addEventListener('mouseup', handleMouseUp);
        element.addEventListener('touchstart', handleTouchStart);
        element.addEventListener('touchmove', handleTouchMove);
        element.addEventListener('touchend', handleTouchEnd);
        mouseScrollEndInertiaUtil.on('inertiaMove', handleInertiaMove);
        mouseWheelEndInertiaUtil.on('inertiaMove', handleInertiaMove);
        touchEndInertiaUtil.on('inertiaMove', handleInertiaMove);
    };
    const unbind = () => {
        element.removeEventListener('keydown', handleKeydown);
        element.removeEventListener(eventMouseWheel, handleMousewheel);
        element.removeEventListener('mousedown', handleMouseDown);
        element.removeEventListener('mousemove', handleMouseMove);
        element.removeEventListener('mouseup', handleMouseUp);
        element.removeEventListener('touchstart', handleTouchStart);
        element.removeEventListener('touchmove', handleTouchMove);
        element.removeEventListener('touchend', handleTouchEnd);
        mouseWheelEndInertiaUtil.stop();
        mouseWheelEndInertiaUtil.clear();
        mouseScrollEndInertiaUtil.stop();
        mouseScrollEndInertiaUtil.clear();
        touchEndInertiaUtil.stop();
        touchEndInertiaUtil.clear();
    };
    bind();
    return mix(signal, {
        options(config = {}) {
            Object.assign(options, config);
            return this;
        },
        get scrollTop() {
            return scrollTop;
        },
        set scrollTop(y) {
            scrollTop = clamp(y, options.minScrollTop, options.maxScrollTop);
        },
        set minScrollTop(y) {
            options.minScrollTop = y;
            scrollTop = clamp(scrollTop, options.minScrollTop, options.maxScrollTop);
        },
        set maxScrollTop(y) {
            options.maxScrollTop = y;
            scrollTop = clamp(scrollTop, options.minScrollTop, options.maxScrollTop);
        },
        get scrollLeft() {
            return scrollLeft;
        },
        set scrollLeft(x) {
            scrollLeft = clamp(x, options.minScrollLeft, options.maxScrollLeft);
        },
        set minScrollLeft(x) {
            options.minScrollLeft = x;
            scrollLeft = clamp(scrollLeft, options.minScrollLeft, options.maxScrollLeft);
        },
        set maxScrollLeft(x) {
            options.maxScrollLeft = x;
            scrollLeft = clamp(scrollLeft, options.minScrollLeft, options.maxScrollLeft);
        },
        pause() {
            paused = true;
            return this;
        },
        resume() {
            paused = false;
            return this;
        },
        destroy() {
            unbind();
        },
    });
}
;
//# sourceMappingURL=index.js.map